/**
 * Plain-language helpers for the skill pages: dates, confirm-before areas,
 * timezones, triggers, a run's outcome, the starter gallery and the skills
 * list's search, filter and sort.
 */
import {
  BadgeCheck,
  BookOpen,
  Briefcase,
  Calculator,
  CalendarClock,
  ClipboardCheck,
  FileText,
  Inbox,
  Languages,
  Link2,
  ListChecks,
  PackageCheck,
  RefreshCw,
  Scale,
  ShieldAlert,
  Users,
  type LucideIcon
} from "lucide-react";

import { VISAVALUE } from "@/types/visa.type";

import type {
  FirmSkill,
  FirmSkillStatus,
  RunOutcome,
  RunOutcomeStatus,
  SkillLastRun,
  SkillOptionEvent,
  SkillOptions,
  SkillScheduleFreq,
  SkillTrigger,
  SkillTriggerEdit
} from "@/features/firmKnowledge/types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

/** "Oct 1" this year, "Oct 1, 2025" otherwise; "" when unknown. */
export const shortDate = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const day = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return date.getFullYear() === now.getFullYear()
    ? day
    : `${day}, ${date.getFullYear()}`;
};

/** "Mon, Oct 19, 2026, 9:00 AM": the full timestamp for a date's `title`. */
export const fullDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/** "Mon, Oct 19, 9:00 AM" (the year only when it isn't this one). */
export const whenLabel = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit"
  });
};

/** "Updated by Andrew · Oct 1" (or "Updated Oct 1" when the name is unknown). */
export const updatedLine = (
  skill: Pick<
    FirmSkill,
    "updated_at" | "created_at" | "updated_by_name" | "created_by_name"
  >,
  now?: Date
): string => {
  const when = shortDate(skill.updated_at ?? skill.created_at, now);
  const who = skill.updated_by_name ?? skill.created_by_name;
  if (!when) return who ? `Updated by ${who}` : "";
  return who ? `Updated by ${who} · ${when}` : `Updated ${when}`;
};

/** A confirm-before area as a label: "drafting documents" -> "Drafting documents". */
export const areaLabel = (area: string): string =>
  area ? area.charAt(0).toUpperCase() + area.slice(1) : area;

export const TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "America/New_York", label: "Eastern time" },
  { value: "America/Chicago", label: "Central time" },
  { value: "America/Denver", label: "Mountain time" },
  { value: "America/Phoenix", label: "Arizona time" },
  { value: "America/Los_Angeles", label: "Pacific time" },
  { value: "America/Anchorage", label: "Alaska time" },
  { value: "Pacific/Honolulu", label: "Hawaii time" },
  { value: "America/Puerto_Rico", label: "Atlantic time (Puerto Rico)" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Central Europe" },
  { value: "Asia/Kolkata", label: "India" },
  { value: "Asia/Shanghai", label: "China" },
  { value: "Asia/Tokyo", label: "Japan" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "UTC", label: "UTC" }
];

/** "Eastern time"; the city part of the zone for one not in the list. */
export const timezoneLabel = (zone: string): string =>
  TIMEZONES.find((z) => z.value === zone)?.label ??
  (zone.split("/").pop() ?? zone).replace(/_/g, " ");

/** The list, plus `current` (and the browser's zone) when they're not in it. */
export const timezoneChoices = (
  current: string,
  browser: string
): Array<{ value: string; label: string }> => {
  const out = [...TIMEZONES];
  for (const zone of [browser, current]) {
    if (zone && !out.some((z) => z.value === zone)) {
      out.unshift({ value: zone, label: timezoneLabel(zone) });
    }
  }
  return out;
};

/** "09:00" -> "9:00 AM". */
export const clockLabel = (time: string): string => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time || "");
  if (!match) return time;
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${match[2]} ${suffix}`;
};

export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export const FREQ_LABEL: Record<SkillScheduleFreq, string> = {
  daily: "Every day",
  weekdays: "Every weekday",
  weekly: "Every week",
  monthly: "Every month"
};

/** "Every Monday at 9:00 AM Eastern time". */
export const describeSchedule = (t: Pick<SkillTriggerEdit, "schedule">) => {
  const s = t.schedule;
  if (!s) return "";
  const at = `at ${clockLabel(s.time)} ${timezoneLabel(s.timezone)}`;
  switch (s.freq) {
    case "daily":
      return `Every day ${at}`;
    case "weekdays":
      return `Every weekday ${at}`;
    case "weekly":
      return `Every ${WEEKDAYS[s.weekday] ?? "Monday"} ${at}`;
    case "monthly":
      return `On day ${s.day_of_month} of each month ${at}`;
    default:
      return at;
  }
};

const lowerFirst = (text: string) =>
  text ? text.charAt(0).toLowerCase() + text.slice(1) : text;

/**
 * The actions the morning sweep starts, with their default window (null:
 * not windowed; the priority date fires when a Visa Bulletin makes it current).
 */
export const DATE_ACTIONS: Record<string, number | null> = {
  "notice.response_due": 14,
  "client.document_expiring": 90,
  "client.priority_date_current": null
};

export const isDateAction = (action: string | null | undefined): boolean =>
  Boolean(action && action in DATE_ACTIONS);

/**
 * Every action a skill can listen for, as `SKILL_EVENT_ACTIONS` in
 * `models_firm_skill.py` labels it: what the trigger picker and the list show
 * until (or if) `/skills/options` answers.
 */
export const SKILL_EVENTS: readonly SkillOptionEvent[] = [
  {
    action: "rfe_extraction.created",
    label: "An RFE, NOID or NOIR is read into the case",
    kind: "activity"
  },
  {
    action: "notice.response_due",
    label: "An RFE or NOID response is coming due",
    kind: "date",
    default_days: 14
  },
  {
    action: "client.document_expiring",
    label: "A client document is about to expire",
    kind: "date",
    default_days: 90
  },
  {
    action: "client.priority_date_current",
    label: "The client's priority date becomes current",
    kind: "date",
    default_days: null
  },
  {
    action: "case.uscis_receipt.added",
    label: "A USCIS receipt is added",
    kind: "activity"
  },
  {
    action: "case.status_updated",
    label: "The case's status changes",
    kind: "activity"
  },
  {
    action: "document.uploaded",
    label: "A document is uploaded to the case",
    kind: "activity"
  },
  {
    action: "outgoing_intake.submitted",
    label: "The client submits an intake",
    kind: "activity"
  },
  { action: "form.filled", label: "A USCIS form is filled", kind: "activity" },
  {
    action: "packet.exported",
    label: "A filing packet is downloaded",
    kind: "activity"
  },
  {
    action: "case.company_approval_answered",
    label: "The employer answers an approval request",
    kind: "activity"
  },
  {
    action: "case.company_message_received",
    label: "The employer sends a message",
    kind: "activity"
  }
];

/** The options' events, else the built-in list. */
export const skillEvents = (
  options: Pick<SkillOptions, "events"> | null | undefined
): readonly SkillOptionEvent[] =>
  options?.events?.length ? options.events : SKILL_EVENTS;

/** An action's label from the options, then the built-in list, then the code. */
export const eventLabelOf = (
  options: Pick<SkillOptions, "events"> | null | undefined,
  action: string
): string =>
  options?.events?.find((e) => e.action === action)?.label ??
  SKILL_EVENTS.find((e) => e.action === action)?.label ??
  action;

export const EXPIRATION_ACTION = "client.document_expiring";
export const PRIORITY_ACTION = "client.priority_date_current";

/** The Visa Bulletin charts a priority-date automation reads. */
export const PRIORITY_CHART_LABEL: Record<string, string> = {
  final_action: "Final Action",
  dates_for_filing: "Dates for Filing"
};

export const chartOf = (t: SkillTriggerEdit): string =>
  t.event?.chart ?? "final_action";

/** Fallback labels for the documents an expiration automation watches. */
export const DOCUMENT_KIND_LABEL: Record<string, string> = {
  i94: "I-94",
  status: "Status",
  ead: "EAD",
  advance_parole: "Advance Parole",
  passport: "Passport",
  visa: "Visa"
};

/** 14 -> "2 weeks"; 7 -> "1 week"; otherwise "N days". */
export const daysLabel = (days: number): string => {
  if (days && days % 7 === 0 && days <= 28) {
    const weeks = days / 7;
    return weeks === 1 ? "1 week" : `${weeks} weeks`;
  }
  return days === 1 ? "1 day" : `${days} days`;
};

/** The autonomy a trigger has, with the backend's defaults. */
export const autonomyOf = (t: SkillTriggerEdit): "propose" | "act" =>
  t.autonomy ??
  (t.kind === "event" && isDateAction(t.event?.action) ? "propose" : "act");

/**
 * One trigger in a sentence: "When an RFE is read into the case, on EB-1A
 * cases: plans first and waits." `eventLabel` maps an action to its label.
 * Worded like the SKILL.md's "How this skill starts" (`describe_trigger`).
 */
export const describeTrigger = (
  t: SkillTriggerEdit,
  eventLabel: (action: string) => string,
  kindLabel: (kind: string) => string = (k) => DOCUMENT_KIND_LABEL[k] ?? k
): string => {
  const action = t.event?.action ?? "";
  let when =
    t.kind === "schedule"
      ? describeSchedule(t)
      : `When ${lowerFirst(eventLabel(action))}`;
  if (t.kind === "event" && isDateAction(action)) {
    if (action === EXPIRATION_ACTION) {
      const kinds = (t.event?.kinds ?? []).map(kindLabel);
      const docs = kinds.length ? kinds.join(", ") : "any document";
      const deps =
        t.event?.include_dependents === false
          ? "not dependents"
          : "dependents too";
      when += ` (${docs}; ${deps})`;
    }
    if (action === PRIORITY_ACTION) {
      const deps =
        t.event?.include_dependents === false ? "; not dependents" : "";
      when += ` (${PRIORITY_CHART_LABEL[chartOf(t)] ?? "Final Action"}${deps})`;
    } else {
      when += ` within ${daysLabel(
        t.event?.days_before ?? DATE_ACTIONS[action] ?? 14
      )}`;
    }
  }
  const statuses = (t.case_filter?.statuses ?? []).map((s) =>
    s.replace(/_/g, " ").toLowerCase()
  );
  const visas = t.case_filter?.visa_types ?? [];
  const parts = [
    statuses.length ? statuses.join(" or ") : "",
    visas.length ? visas.join(", ") : ""
  ].filter(Boolean);
  const which = parts.length
    ? `, on ${parts.join(" ")} cases`
    : t.kind === "schedule"
    ? ", on every open case"
    : "";
  let how =
    (t.mode ?? "plan") === "plan" ? "plans first and waits" : "does the work";
  if (autonomyOf(t) === "propose") {
    how += ", proposing changes for the attorney to approve";
  }
  return `${when}${which}: ${how}.`;
};

/**
 * What to say about a completed run whose report leaves out something the
 * skill's Result promises; null when there is nothing to flag.
 */
export const outcomeNote = (
  outcome: RunOutcome | null | undefined
): string | null => {
  if (!outcome || outcome.status !== "partial" || !outcome.missing.length) {
    return null;
  }
  const items = outcome.missing;
  const list =
    items.length === 1
      ? items[0]
      : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
  return `The automation's result promises ${list}, but the run's report doesn't mention ${
    items.length === 1 ? "it" : "them"
  }. Check the case and rate the run.`;
};

/** How a run ended, as the agent reported it (`report_run_outcome`). */
export const RUN_OUTCOME_LABEL: Record<RunOutcomeStatus, string> = {
  done: "Done",
  no_action: "Nothing Needed",
  needs_attorney: "Needs the Attorney",
  blocked: "Blocked"
};

const RUN_STATUS_WORD: Record<string, string> = {
  running: "Running",
  needs_input: "Needs Your Answer",
  needs_approval: "Needs Approval",
  completed: "Completed",
  stopped: "Stopped",
  failed: "Failed"
};

/** The list's Last Run cell: "Oct 10 · Nothing Needed"; "" with no run. */
export const lastRunLabel = (
  last: SkillLastRun | null | undefined,
  now?: Date
): string => {
  if (!last?.run_id) return "";
  const when = shortDate(last.started_at, now);
  const how = last.outcome_status
    ? RUN_OUTCOME_LABEL[last.outcome_status]
    : last.status
    ? RUN_STATUS_WORD[last.status] ?? ""
    : "";
  return [when, how].filter(Boolean).join(" · ");
};

/**
 * The list's Last Used cell: the newest run, or else the last time an agent
 * followed it or an attorney picked it, with how often that has happened.
 */
export const lastUseLabel = (
  skill: Pick<FirmSkill, "last_run" | "use_count" | "last_used_at">,
  now?: Date
): string => {
  const run = lastRunLabel(skill.last_run, now);
  const used = skill.last_used_at ?? null;
  const runAt = Date.parse(skill.last_run?.started_at ?? "");
  if (run && (!used || runAt >= Date.parse(used))) return run;
  if (!used) return run;
  const count = skill.use_count ?? 0;
  const times = count > 1 ? `Used ${count} Times` : "Used Once";
  return [shortDate(used, now), times].filter(Boolean).join(" · ");
};

// -- Starter gallery ----------------------------------------------------------

export type StarterCategory =
  | "Case Review"
  | "Notices and Deadlines"
  | "Employment"
  | "Family"
  | "Intake";

export const STARTER_CATEGORIES: readonly StarterCategory[] = [
  "Case Review",
  "Notices and Deadlines",
  "Employment",
  "Family",
  "Intake"
];

/**
 * What a starter's automation listens for; `blankSkill` fills in the rest the
 * way Settings' "Add Trigger" does. Actions are `SKILL_EVENT_ACTIONS` in
 * `models_firm_skill.py`.
 */
export type StarterTrigger =
  | {
      kind: "event";
      action: string;
      /** Date actions: start this many days before the date. */
      days_before?: number;
      kinds?: string[];
      visa_types?: string[];
    }
  | { kind: "schedule"; freq: SkillScheduleFreq; visa_types?: string[] };

export type StarterSkill = {
  id: string;
  title: string;
  /** The card's two lines, and the new skill's description. */
  blurb: string;
  /** The new skill's SKILL.md body. */
  instructions: string;
  /** The automation the new skill starts with; none for "Run on a case". */
  trigger?: StarterTrigger;
  category: StarterCategory;
  icon: LucideIcon;
  /** What sets it off, and what it produces: "RFE added" -> "Issue List · Outline". */
  when: string;
  makes: string;
  /**
   * Case visas this template is for on the Explore board. Empty means any
   * case (including notice workflows that apply broadly). Trigger
   * `visa_types` are merged for filtering.
   */
  visa_types?: string[];
};

const steps = (...lines: string[]) =>
  lines.map((line, i) => `${i + 1}. ${line}`).join("\n");

export const STARTER_SKILLS: readonly StarterSkill[] = [
  {
    id: "case-assessment",
    title: "Case Assessment",
    blurb:
      "Review the uploaded documents against this visa or green card, judge whether the person qualifies, and list what still needs to be collected.",
    instructions: steps(
      "Review the documents uploaded to the case.",
      "Check them against the criteria of this visa or green card. For criteria- or prong-based cases (EB-1, NIW, O-1, I-601, I-589, L-1), map each document to the requirement it supports and say how strong that evidence is.",
      "Go through every requirement. For each criterion or prong, judge whether the individual qualifies.",
      "Write a short case assessment for the attorney.",
      "Suggest a checklist of what still needs to be collected."
    ),
    category: "Case Review",
    icon: ClipboardCheck,
    when: "Run on a case",
    makes: "Assessment · Checklist"
  },
  {
    id: "packet-checker",
    title: "Packet Checker",
    blurb:
      "Check the filing package against the final USCIS mailing checklist before it goes out.",
    instructions: steps(
      "Check each USCIS form's edition date against the edition USCIS currently accepts on uscis.gov/forms. The edition date is not an expiration date. If it does not match, the form has to be downloaded and refilled.",
      "Confirm every form is complete: all pages printed and in order, nothing cut off, faded, or illegible, the same edition date on every page, and every required field filled.",
      "Confirm every required signature is handwritten in ink, with its date filled in. Applicants 14 or older sign their own forms. A parent or legal guardian may sign for a child under 14. Electronic, typed, stamped, or DocuSign signatures are not accepted.",
      "Calculate the filing fee on uscis.gov/fees the day the package is prepared. Pay by Form G-1450 (U.S. card) or Form G-1650 (U.S. bank account). Do not send a check or money order. Pay each benefit request separately, do not include both payment forms, and confirm the amount and the account or card can cover it.",
      "Confirm the direct filing address for this form, the client's state, and the filing category, including a different address for USPS and for a courier.",
      "Confirm the package will be mailed with tracking, and that the mailing receipt and proof of delivery will be saved."
    ),
    category: "Case Review",
    icon: PackageCheck,
    when: "Run on a case",
    makes: "Mailing Checklist"
  },
  {
    id: "extension-workflow",
    title: "Extension Workflow",
    blurb:
      "Review the previous filing, say what to drop or update, and start the extension from what still applies.",
    instructions: steps(
      "Review the previous filing.",
      "Suggest documents that can be removed or are no longer needed.",
      "Suggest documents that should be added or updated.",
      "Identify information that needs to be updated from the prior filing.",
      "Carry forward relevant information from the previous filing and begin drafting the extension."
    ),
    category: "Case Review",
    icon: RefreshCw,
    when: "Run on a case",
    makes: "Extension Draft"
  },
  {
    id: "refile-previous-filing",
    title: "Refile From a Previous Filing",
    blurb:
      "Start from a prior packet or case, identify what is stale, and refresh the strategy and drafts with current evidence.",
    instructions: steps(
      "Ask the attorney to upload the previous packet or choose the prior case.",
      "Split the previous packet into exhibits, flag what is out of date, and carry forward relevant letters and strategy.",
      "Identify the evidence that needs to be refreshed. Ask the attorney to upload it or send the client a targeted intake to collect it.",
      "Use the previous drafts as a starting point and refresh them with the newly uploaded evidence."
    ),
    category: "Case Review",
    icon: RefreshCw,
    when: "Run on a case",
    makes: "Exhibits · Refreshed Drafts"
  },
  {
    id: "translation-workflow",
    title: "Identify What Needs Translation",
    blurb:
      "Identify which case documents need translation, duplicate them into a folder at the bottom of the case files, and track what is still awaiting translation.",
    instructions: steps(
      "Review the case documents and identify which documents require translation.",
      "Duplicate documents that need a translation into a new folder at the bottom of the case files, so those copies do not change exhibit or packet numbering. Do not move the originals.",
      "Track which documents are still awaiting translation."
    ),
    category: "Case Review",
    icon: Languages,
    when: "Run on a case",
    makes: "Documents To Translate"
  },
  {
    id: "translate-foreign-documents",
    title: "Translate Foreign Documents",
    blurb:
      "Translate foreign documents into English from copies in a new folder at the bottom of the case files, and file each translation in that same folder with its copy.",
    instructions: steps(
      "Review the case documents and identify which documents require translation.",
      "Duplicate documents that need a translation into a new folder at the bottom of the case files, so those copies do not change exhibit or packet numbering. Do not move the originals.",
      "Translate those copies to English.",
      "Place each English translation in that same bottom folder with its copy. Do not insert the translation into the numbered exhibit sequence."
    ),
    category: "Case Review",
    icon: BookOpen,
    when: "Run on a case",
    makes: "English Translation"
  },
  {
    id: "match-translations",
    title: "Match Translations",
    blurb:
      "Pair each translation with its original, and flag a translation or a source document that is missing its match.",
    instructions: steps(
      "Match translated documents to the corresponding original-language documents.",
      "Flag missing translations, and translations that have no corresponding source document.",
      "Keep each original and its translation paired for packet preparation."
    ),
    category: "Case Review",
    icon: Link2,
    when: "Run on a case",
    makes: "Pairs · Gaps"
  },
  {
    id: "rfe-risk-report",
    title: "RFE Risk Report",
    blurb:
      "Read the case the way an officer would, and list what you would issue a request for evidence on.",
    instructions: steps(
      "Read the case the way a USCIS officer would.",
      "List what you would issue a request for evidence on, and why the record does not yet meet that requirement.",
      "For each item, name the evidence that is missing or weak."
    ),
    category: "Case Review",
    icon: ShieldAlert,
    when: "Run on a case",
    makes: "RFE Risks"
  },
  {
    id: "inbox-to-case",
    title: "Inbox To Case",
    blurb:
      "Match a client's messages and attachments to their CaseBlink case, then bring in what is missing from the record.",
    instructions: steps(
      "Check whether the firm's email integration is connected. If it is not, tell the attorney that inbox search is unavailable until it is connected.",
      "Search the connected inbox for the client's emails and match them to the CaseBlink case.",
      "Bring the matching attachments into the case, or list what is not yet in the system of record.",
      "Update the case record with the new information and documents."
    ),
    category: "Case Review",
    icon: Inbox,
    when: "Run on a case",
    makes: "Case Updates · Attachments"
  },
  {
    id: "forms-automation",
    title: "Forms Automation",
    blurb:
      "Fill forms from the case record, ask only for what is missing, and flag conflicts before filing.",
    instructions: steps(
      "Fill the forms with all information currently available in the case.",
      "Create a list of every missing field or piece of information.",
      "Generate a targeted intake with only the specific questions needed to complete the forms.",
      "Once the intake is completed, update the forms automatically.",
      "Flag inconsistent answers across forms, documents, and prior filings."
    ),
    category: "Case Review",
    icon: FileText,
    when: "Run on a case",
    makes: "Completed Forms · Targeted Intake"
  },
  {
    id: "cspa-analysis",
    title: "CSPA Analysis",
    blurb:
      "Calculate the CSPA age, including petition-pending time where it applies, and flag dates or issues that affect eligibility.",
    instructions: steps(
      "Calculate the CSPA age, including petition-pending time where it applies.",
      "Flag the key dates and any issues that affect CSPA eligibility.",
      "List any date or document still needed to complete the calculation."
    ),
    category: "Case Review",
    icon: Calculator,
    when: "Run on a case",
    makes: "CSPA Age · Eligibility Flags"
  },
  {
    id: "rfe-kickoff",
    title: "RFE Response Kickoff",
    blurb:
      "Read the RFE, list each issue with its deadline, map the evidence we have, and draft the response outline.",
    instructions: steps(
      "Read the RFE into the case.",
      "List every issue USCIS raised, with the response deadline.",
      "Map each issue to the evidence already in the case files, and flag what's missing.",
      "Draft an outline of the response from the firm's RFE response template for the attorney to review."
    ),
    trigger: { kind: "event", action: "rfe_extraction.created" },
    category: "Notices and Deadlines",
    icon: FileText,
    when: "RFE added",
    makes: "Issue List · Outline"
  },
  {
    id: "receipt-notice",
    title: "Receipt Notice: Calendar and Client Update",
    blurb:
      "Read the receipt, add the case to tracking and calendar the dates, then draft a plain update to the client.",
    instructions: steps(
      "Read the receipt number, form, priority date and service center from the notice, and record them on the case.",
      "Add reminders for the next steps and the expected processing times.",
      "Draft a short, plain-English update to the client for the attorney to approve."
    ),
    trigger: { kind: "event", action: "case.uscis_receipt.added" },
    category: "Notices and Deadlines",
    icon: CalendarClock,
    when: "Receipt notice added",
    makes: "Calendar · Client Email"
  },
  {
    id: "deadline-reminder-builder",
    title: "Deadline And Reminder Builder",
    blurb:
      "Read approvals and I-94s to track the true expiry and the reminders that should happen before it.",
    instructions: steps(
      "Read the approval notices and I-94s in the case.",
      "Record the true expiry date and set the related reminder dates, including a 90-day call-up.",
      "Set up priority-date tracking using the Visa Bulletin.",
      "List any missing approval notice, I-94, or date needed to complete the reminders."
    ),
    category: "Notices and Deadlines",
    icon: CalendarClock,
    when: "Run on a case",
    makes: "Deadlines · Reminders"
  },
  {
    id: "uscis-case-approved",
    title: "USCIS Case Approved",
    blurb:
      "Notice when USCIS has approved the case, update the status, and list the post-approval steps and notices.",
    instructions: steps(
      "Detect when a USCIS case has been approved.",
      "Update the case status.",
      "Identify any post-approval steps.",
      "Generate the client and team notifications or tasks that should follow the approval."
    ),
    category: "Notices and Deadlines",
    icon: BadgeCheck,
    when: "Run on a case",
    makes: "Status · Next Steps"
  },
  {
    id: "noid-response",
    title: "NOID Response Plan",
    blurb:
      "Read the NOID, list each ground with its deadline, and plan the rebuttal and the evidence to gather.",
    instructions: steps(
      "Read the Notice of Intent to Deny into the case.",
      "List every ground USCIS gave, with the response deadline.",
      "Map each ground to the evidence already in the case files, and flag what's missing.",
      "Draft a rebuttal plan, ground by ground, for the attorney to review."
    ),
    trigger: { kind: "event", action: "rfe_extraction.created" },
    category: "Notices and Deadlines",
    icon: FileText,
    when: "NOID added",
    makes: "Grounds · Rebuttal Plan"
  },
  {
    id: "h1b-extension",
    title: "H-1B Extension Checklist",
    blurb:
      "Confirm dates and maintenance of status, request what the employer and employee owe, and prep the I-129.",
    instructions: steps(
      "Confirm the current I-797 and I-94 dates.",
      "Check maintenance of status from the recent pay stubs.",
      "Send the employer and the employee a checklist of what we need from each of them.",
      "Start the I-129 extension with the LCA details."
    ),
    trigger: {
      kind: "event",
      action: "client.document_expiring",
      days_before: 180,
      kinds: ["status"],
      visa_types: ["H-1B"]
    },
    category: "Employment",
    icon: Briefcase,
    when: "Six months before status ends",
    makes: "Checklist · I-129"
  },
  {
    id: "h1b-recapture",
    title: "H-1B Recapture",
    blurb:
      "Calculate time spent outside the United States that may be recaptured and the resulting maximum H-1B end date.",
    instructions: steps(
      "Review the H-1B approval history and the current H-1B end date.",
      "Identify and total documented time the beneficiary spent outside the United States that may be recaptured.",
      "Calculate the resulting maximum H-1B end date, and show the dates and assumptions used.",
      "List the travel records or other evidence still needed to support the calculation."
    ),
    category: "Employment",
    icon: Briefcase,
    when: "Run on a case",
    makes: "Recapture Calculation",
    visa_types: [VISAVALUE.H1B]
  },
  {
    id: "perm-recruitment",
    title: "PERM Recruitment Tracker",
    blurb:
      "Track each recruitment step and its dates, and warn before the filing window closes.",
    instructions: steps(
      "Check which recruitment steps are done and when each one ran.",
      "List what's still needed before the ETA-9089 can be filed.",
      "Warn the attorney when the filing window is close to opening or closing.",
      "Draft a short status note to the employer."
    ),
    trigger: { kind: "schedule", freq: "weekly", visa_types: ["PERM"] },
    category: "Employment",
    icon: ListChecks,
    when: "Every week",
    makes: "Status · Reminders"
  },
  {
    id: "i130-i485",
    title: "I-130 / I-485 Concurrent Packet",
    blurb:
      "Check the family-based packet is complete, fill the forms from the questionnaire, and build the exhibit list.",
    instructions: steps(
      "Check we have every document for a concurrent I-130, I-485, I-765, I-131 and I-864 filing.",
      "Fill the forms from the client's questionnaire.",
      "List anything missing for the client.",
      "Build the exhibit list and divider tabs the way the firm does them."
    ),
    category: "Family",
    icon: Users,
    when: "Run on a case",
    makes: "Forms · Exhibit List",
    visa_types: [VISAVALUE.I130, VISAVALUE.AOS]
  },
  {
    id: "marriage-inconsistency-checker",
    title: "Marriage-Based Inconsistency Checker",
    blurb:
      "Compare addresses, dates, work, and marital history across the marriage-based record, and report what does not match.",
    instructions: steps(
      "Compare addresses and residence history across the forms, documents, and supporting evidence.",
      "Identify inconsistent dates, addresses, employment information, marital history, or other key facts.",
      "Produce an inconsistency report for the attorney to review before filing."
    ),
    category: "Family",
    icon: Scale,
    when: "Run on a case",
    makes: "Inconsistency Report",
    visa_types: [VISAVALUE.I130, VISAVALUE.AOS, VISAVALUE.I751]
  },
  {
    id: "k1-interview",
    title: "K-1 Interview Prep",
    blurb:
      "Build the consular interview checklist from the case, and send the couple a plain-English prep note.",
    instructions: steps(
      "Build the checklist of documents the beneficiary must bring to the consular interview, from what's in the case.",
      "List anything missing.",
      "Draft a plain-English prep note for the couple with the likely interview questions, for the attorney to approve."
    ),
    category: "Family",
    icon: Users,
    when: "Run on a case",
    makes: "Checklist · Client Email",
    visa_types: [VISAVALUE.K1]
  },
  {
    id: "intake-follow-up",
    title: "Intake Follow-Up",
    blurb:
      "When the client submits an intake, review the answers, chase what's missing, and summarize the case for the attorney.",
    instructions: steps(
      "Review the intake answers and the uploaded documents.",
      "List anything missing or inconsistent.",
      "Draft a friendly follow-up asking the client for it.",
      "Write a one-page case summary with the likely visa options for the attorney."
    ),
    trigger: { kind: "event", action: "outgoing_intake.submitted" },
    category: "Intake",
    icon: Inbox,
    when: "Intake submitted",
    makes: "Follow-Up · Case Summary"
  }
];

/** A starter's automation as an unsaved trigger (no id, never fired). */
export const starterTrigger = (
  t: StarterTrigger,
  timezone: string
): SkillTrigger => {
  const base: SkillTrigger = {
    id: "",
    kind: t.kind,
    enabled: true,
    run_as_uid: null,
    mode: "plan",
    case_filter: { statuses: [], visa_types: t.visa_types ?? [] },
    schedule: null,
    event: null,
    last_fired_at: null,
    next_fire_at: null,
    last_error: null
  };
  if (t.kind === "schedule") {
    return {
      ...base,
      schedule: {
        freq: t.freq,
        time: "09:00",
        weekday: 0,
        day_of_month: 1,
        timezone
      }
    };
  }
  if (!isDateAction(t.action)) {
    return { ...base, event: { action: t.action, cooldown_hours: 24 } };
  }
  return {
    ...base,
    event: {
      action: t.action,
      cooldown_hours: 24,
      days_before: t.days_before ?? DATE_ACTIONS[t.action] ?? 14,
      kinds: t.kinds ?? [],
      include_dependents: false
    },
    autonomy: "propose",
    optimize_for: "cost"
  };
};

export const starterById = (
  id: string | null | undefined
): StarterSkill | null => STARTER_SKILLS.find((s) => s.id === id) ?? null;

/**
 * A skill that isn't saved yet: blank, or filled from a starter. The skill
 * page edits it like any other, and its first Save creates it.
 */
export const blankSkill = (
  starter: StarterSkill | null,
  { lawFirmId, timezone }: { lawFirmId: string; timezone: string }
): FirmSkill => ({
  id: "",
  law_firm_id: lawFirmId,
  slug: "",
  title: starter?.title ?? "",
  description: starter?.blurb ?? "",
  // The card's blurb, not a line the attorney wrote. Left false, Save would
  // send it as hand-written and CaseBlink would stop rewriting it.
  description_auto: Boolean(starter),
  instructions: starter?.instructions ?? "",
  references: [],
  inputs: [],
  authority: { default_mode: "agent", confirm_before: [], notes: "" },
  uses_skills: [],
  triggers: starter?.trigger ? [starterTrigger(starter.trigger, timezone)] : [],
  status: "active",
  agents: ["case"],
  source: "manual",
  source_thread_id: null,
  builder_thread_id: null,
  starter_id: starter?.id ?? null,
  version: 0,
  created_by: null,
  created_at: null,
  updated_by: null,
  updated_at: null
});

/**
 * Duplicate: an unsaved copy titled "X (Copy)", a draft, with the triggers
 * kept (as new triggers, never fired).
 */
export const copySkill = (skill: FirmSkill): FirmSkill => ({
  ...skill,
  id: "",
  slug: "",
  title: `${skill.title || skill.slug} (Copy)`,
  triggers: (skill.triggers ?? []).map((t) => ({
    ...t,
    id: "",
    last_fired_at: null,
    next_fire_at: null,
    last_error: null
  })),
  status: "draft",
  source: "manual",
  source_thread_id: null,
  builder_thread_id: null,
  starter_id: null,
  last_run: null,
  version: 0,
  created_by: null,
  created_at: null,
  created_by_name: null,
  updated_by: null,
  updated_at: null,
  updated_by_name: null,
  markdown: undefined,
  suggested_automations: undefined
});

/** The built-in starters the Firm tab shows above the table. */
export const FEATURED_STARTERS: readonly string[] = [
  "rfe-kickoff",
  "receipt-notice",
  "h1b-extension"
];

/**
 * A trigger as a short chip: "Every week" for a schedule, the event's label
 * ("An RFE is added") otherwise.
 */
export const triggerChip = (
  t: Pick<SkillTriggerEdit, "kind" | "schedule" | "event">,
  eventLabel: (action: string) => string
): string => {
  if (t.kind === "schedule") {
    return t.schedule
      ? FREQ_LABEL[t.schedule.freq] ?? "On a schedule"
      : "On a schedule";
  }
  const action = t.event?.action ?? "";
  return action ? eventLabel(action) : "When something happens";
};

// -- Skills list --------------------------------------------------------------

export type SkillStatusFilter = "" | FirmSkillStatus;
export type SkillSort = "updated" | "title";

export const SKILL_STATUS_LABEL: Record<FirmSkillStatus, string> = {
  active: "Enabled",
  draft: "Draft",
  archived: "Archived"
};

const skillVisaWords = (skill: FirmSkill): string[] =>
  (skill.triggers ?? []).flatMap((t) => t.case_filter?.visa_types ?? []);

const searchable = (skill: FirmSkill): string =>
  [skill.title, skill.slug, skill.description, ...skillVisaWords(skill)]
    .join(" ")
    .toLowerCase();

/** Skills matching every word of `query` and the status, sorted. */
export const filterSkills = (
  skills: FirmSkill[],
  {
    query,
    status,
    sort
  }: { query: string; status: SkillStatusFilter; sort: SkillSort }
): FirmSkill[] => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const kept = skills.filter(
    (s) =>
      (!status || s.status === status) &&
      words.every((w) => searchable(s).includes(w))
  );
  const time = (s: FirmSkill) =>
    Date.parse(s.updated_at ?? s.created_at ?? "") || 0;
  return [...kept].sort((a, b) =>
    sort === "title"
      ? (a.title || a.slug).localeCompare(b.title || b.slug)
      : time(b) - time(a)
  );
};
